import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import type { PublicMitMandateDTO, PublicMitSavedCardDTO } from '../../../core/models';
import {
  MIT_LIMIT_ROWS,
  MIT_MANDATE_STATUS_LABELS,
  MitService,
  mitApiError,
  mitLimitValue,
  mitMandateStatusSeverity
} from '../../../services/mit.service';
import { PublicMitShellComponent } from '../public-mit-shell/public-mit-shell.component';

@Component({
  selector: 'app-public-mit-manage',
  templateUrl: './public-mit-manage.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, PublicMitShellComponent]
})
export class PublicMitManageComponent {
  private route = inject(ActivatedRoute);
  private mitService = inject(MitService);

  private id = Number(this.route.snapshot.paramMap.get('id'));
  private hash = this.route.snapshot.queryParamMap.get('hash');

  loading = signal(true);
  invalid = signal(false);
  mandate = signal<PublicMitMandateDTO | null>(null);
  confirmOpen = signal(false);
  revoking = signal(false);
  error = signal<string | null>(null);
  savedCards = signal<PublicMitSavedCardDTO[]>([]);
  deletingCardId = signal<number | null>(null);
  changingCard = signal(false);
  cardsError = signal<string | null>(null);
  cardToDelete = signal<PublicMitSavedCardDTO | null>(null);
  deleteCardError = signal<string | null>(null);

  statusLabel = computed(() => {
    const status = this.mandate()?.status;
    return status ? MIT_MANDATE_STATUS_LABELS[status] : '';
  });
  statusClasses = computed(() => {
    const severity = mitMandateStatusSeverity(this.mandate()?.status);
    if (severity === 'success') return 'bg-emerald-100 text-emerald-700';
    if (severity === 'warn') return 'bg-amber-100 text-amber-700';
    if (severity === 'danger') return 'bg-red-100 text-red-700';
    if (severity === 'info') return 'bg-sky-100 text-sky-700';
    return 'bg-slate-200 text-slate-700';
  });
  canRevoke = computed(() => {
    const status = this.mandate()?.status;
    return status === 'ACTIVE' || status === 'SUSPENDED' || status === 'PENDING_ENROLLMENT';
  });
  cardBrandLabel = computed(() => {
    const brand = this.mandate()?.currentCardBrand?.trim();
    if (!brand) return '';
    const key = brand.toUpperCase().replace(/[\s_-]/g, '');
    if (key === 'VISA') return 'Visa';
    if (key === 'MASTERCARD' || key === 'MC') return 'Mastercard';
    if (key === 'AMEX' || key === 'AMERICANEXPRESS') return 'American Express';
    if (key === 'CARNET') return 'Carnet';
    return brand;
  });
  cardDetails = computed(() => {
    const m = this.mandate();
    if (!m) return '';
    const type = m.currentCardType?.trim().toUpperCase();
    const typeLabel = type === 'CREDIT' || type === 'CREDITO' ? 'Crédito' : type === 'DEBIT' || type === 'DEBITO' ? 'Débito' : m.currentCardType;
    const expiry = m.currentCardExpirationMonth && m.currentCardExpirationYear
      ? `Vence ${m.currentCardExpirationMonth}/${m.currentCardExpirationYear.slice(-2)}`
      : null;
    return [m.currentCardBank, typeLabel, expiry].filter(Boolean).join(' · ');
  });
  canChangeCard = computed(() => {
    const status = this.mandate()?.status;
    return status === 'ACTIVE' || status === 'SUSPENDED';
  });
  limitRows = computed(() => {
    const limits = this.mandate()?.limits;
    return MIT_LIMIT_ROWS
      .filter((row) => row.key !== 'maxConsecutiveDeclines' && limits?.[row.key] != null)
      .map((row) => ({ label: row.label, value: mitLimitValue(row, limits) }));
  });

  constructor() {
    if (!this.id || !this.hash) {
      this.loading.set(false);
      this.invalid.set(true);
      return;
    }
    this.mitService.getPublicManage(this.id, this.hash).subscribe({
      next: (response) => {
        this.loading.set(false);
        this.mandate.set(response.body ?? null);
        this.invalid.set(!response.body);
        if (response.body) this.loadSavedCards();
      },
      error: () => {
        this.loading.set(false);
        this.invalid.set(true);
      }
    });
  }

  loadSavedCards(): void {
    this.mitService.savedCardsPublic(this.id, this.hash!).subscribe({
      next: (response) => this.savedCards.set(response.body ?? []),
      error: () => this.savedCards.set([])
    });
  }

  openDeleteCard(card: PublicMitSavedCardDTO): void {
    if (card.inUse || this.deletingCardId() != null) return;
    this.deleteCardError.set(null);
    this.cardToDelete.set(card);
  }

  closeDeleteCard(): void {
    if (this.deletingCardId() != null) return;
    this.cardToDelete.set(null);
  }

  deleteSavedCard(): void {
    const card = this.cardToDelete();
    if (!card || this.deletingCardId() != null) return;
    this.deletingCardId.set(card.id);
    this.deleteCardError.set(null);
    this.mitService.deleteSavedCardPublic(this.id, this.hash!, card.id).subscribe({
      next: (response) => {
        this.deletingCardId.set(null);
        this.cardToDelete.set(null);
        this.savedCards.set(response.body ?? []);
      },
      error: (err: HttpErrorResponse) => {
        this.deletingCardId.set(null);
        this.deleteCardError.set(mitApiError(err, 'No se pudo eliminar la tarjeta').message);
      }
    });
  }

  /** Lleva al cliente a firmar con la tarjeta nueva; la actual sigue vigente hasta que la nueva se valide. */
  changeCard(): void {
    if (this.changingCard()) return;
    this.changingCard.set(true);
    this.cardsError.set(null);
    this.mitService.requestCardReplacementPublic(this.id, this.hash!).subscribe({
      next: (response) => {
        if (response.body) {
          window.location.href = response.body;
          return;
        }
        this.changingCard.set(false);
        this.cardsError.set('No se pudo iniciar el cambio de tarjeta');
      },
      error: (err: HttpErrorResponse) => {
        this.changingCard.set(false);
        this.cardsError.set(mitApiError(err, 'No se pudo iniciar el cambio de tarjeta').message);
      }
    });
  }

  revoke(): void {
    if (this.revoking()) return;
    this.revoking.set(true);
    this.error.set(null);
    this.mitService.revokePublic(this.id, this.hash!).subscribe({
      next: (response) => {
        this.revoking.set(false);
        this.confirmOpen.set(false);
        if (response.body) {
          this.mandate.update((current) => ({ ...current, ...response.body!, recentCharges: response.body!.recentCharges ?? current?.recentCharges }));
        }
      },
      error: (err: HttpErrorResponse) => {
        this.revoking.set(false);
        if (err.status === 403) {
          this.confirmOpen.set(false);
          this.invalid.set(true);
          return;
        }
        this.error.set(mitApiError(err, 'No se pudo revocar la autorización').message);
      }
    });
  }
}
